// views/quizcontent.ts — the real Detox Finder quiz, as shipped.
//
// This is the seed the Content builder opens with. It is copied VERBATIM from
// the design handoff prototype (design_handoff_detox_quiz/Detox Finder Quiz.dc.html,
// `static QUESTIONS/FLOW/INFO/INTROS/THEMES`), which the README names as the
// source of truth for copy, flow, scoring weights, and reason strings.
//
// ⚠️ SCHEMA IS A CONTRACT. The theme reads these exact key names. Two that have
// bitten us already — the old editor wrote `subtitle` (theme reads `sub`) and
// `reasons` as an array (theme reads an AREA-KEYED MAP). Publishing that would
// have silently dropped every question subhead and corrupted every reason chip.
// Don't rename fields here without changing the theme in the same breath.
//
// The one place we deliberately DIVERGE from the prototype is products: its
// handles/prices were placeholders and every one was wrong (handles lacked the
// -boost-blenz suffix → 404 PDPs; $34.50 vs the real $34.00; kit $129 vs $108).
// The values below are the live-verified ones. `price` is a DISPLAY FALLBACK —
// the theme renders real price + compare-at from the Shopify product object, so
// a running discount shows up without anyone editing anything here.

export type QuizArea = "liver" | "kidney" | "parasite" | "metal";

// Illustration slots — ART KEYS, never file paths or URLs.
//
// ⚠️ These MUST stay keys. The theme resolves them against its `data-bbq-art`
// island, which holds real CDN URLs produced by `asset_img_url`. An earlier
// version of this list used theme-relative paths ("assets/ill-organ-support.png")
// and that was a live bug waiting to happen: the theme assigns remote questions
// wholesale (`QUESTIONS = cfg.questions`) with no URL resolution, so a bare path
// would have resolved against the page URL and 404'd every illustration the
// moment anyone hit Publish.
//
// Keys mean the admin never needs to know a filename, a CDN host, or a version
// hash — swapping the artwork stays entirely a Shopify job.
export const IMAGE_SLOTS = [
  { id: "illOrganSupport", label: "Organ support" },
  { id: "illTargetParasites", label: "Target parasites" },
  { id: "illRemoveMetals", label: "Remove metals" },
  { id: "illBindRemove", label: "Bind & remove" },
];

export const AREAS: QuizArea[] = ["liver", "kidney", "parasite", "metal"];

export const AREA_LABEL: Record<string, string> = {
  liver: "Liver", kidney: "Kidney", parasite: "Parasite", metal: "Heavy Metal",
};

export const STARTER: any = {
  version: 1,
  settings: {
    scoreCap: 8,            // raw points that read as a 100% match on the chart
    campaign: "detox-finder",
    incentiveMode: "none",  // none | percent | shipping | both
    incentivePercent: 10,
    incentiveCode: "DETOX10",
    shippingThreshold: 50,
    forceIntro: "auto",     // auto | 0 | 1 | 2 — pins an A/B variant for QA
  },

  // Every button label + the email-capture copy, in one place. These were hard-coded
  // in the theme with no way to change them without a redeploy.
  //
  // ⚠️ KEY NAMES AND SEED VALUES ARE COPIED FROM THE LIVE THEME (verbatim, curly
  // apostrophes and all). The theme shallow-merges this over its own defaults, so a
  // key that doesn't exist there publishes into a void — and a seed that differs from
  // the theme's literal silently changes the storefront the first time anyone hits
  // Publish. If you add a key here, add it to the theme in the same change.
  // See docs/THEME-HANDOFF-quiz-config.md.
  ui: {
    backLabel: "Back",
    continueLabel: "Continue",
    lastStepLabel: "See my results",
    progressWord: "Question",
    exitTitle: "Are you sure?",
    exitBody: "Your answers won’t be saved, and you’d start over next time.",
    exitStay: "No, keep going",
    exitRestart: "I want to start the quiz over",
    exitLeave: "Leave the quiz, I’m done",
    emailCta: "Email me my results",
    emailHide: "Hide form",
    emailHeading: "Email me my results",
    emailIntro: "Keep your plan handy. We’ll send these recommendations and buy links to your inbox so you can decide later without retaking the quiz.",
    emailButton: "Email me my results",
    emailFirstLabel: "First name",
    emailEmailLabel: "Email",
    emailPhoneLabel: "Mobile",
    emailPhoneHint: "(optional — for text reminders)",
    emailConsent: "Keep me posted with detox tips and occasional offers. Your results email sends either way — this just adds you to the list.",
    emailConfirmTitle: "On its way!",
    emailConfirmPrefix: "We just sent your detox plan and buy links to ",
    emailConfirmSuffix: ". It’s saved to your name, so you won’t need to retake the quiz.",
    emailConfirmFallback: "your inbox",
    addToCart: "Add to cart",
    addedLabel: "Added",
    addAll: "Add all",
    addAllSuffix: "to cart",
    viewProduct: "Visit product",
    checkout: "Checkout",
    startOver: "Start over",
    topMatch: "Top match",
    recsLabel: "Recommended for you, in order",
    cartCountSuffix: "in your cart",

    // Results-page section headings. These were inline in the theme's
    // renderResults until 2026-07-19, which is why they never appeared in the
    // Content builder. Values here must stay identical to the theme's UI
    // defaults — a publish overwrites the theme's copy with whatever is here.
    resultsMatchLabel: "How strongly each detox matched your answers",
    scoreHowLabel: "How the score works",
    scoreHowText:
      "Every answer adds points to the detox areas it signals — the more an answer points to buildup in an area, the more it adds. We total the points per area, then show each as a percentage of the highest possible score. Your top match is simply the area with the most points.",
    profileEyebrow: "Your detox profile",
    profileLabel: "What we’re seeing",

    // Results copy — the big title, the "What this means" paragraph, and the
    // one-line reason under each recommended product. Hard-coded in the theme's
    // computeResults() until 2026-07-19; the same strings also drive the results
    // email, which renders exactly what the storefront sends. Editing here
    // changes BOTH surfaces, which is the point — don't reintroduce separate
    // wording on the Klaviyo side.
    //
    // {areas} is filled by the storefront with the shopper's own area words,
    // {amount} with the live kit saving, {products} with the detox names the
    // binder pairs with. Keep the token or that sentence stops personalizing.
    areaWordLiver: "liver",
    areaWordKidney: "kidneys",
    areaWordParasite: "gut & digestion",
    areaWordMetal: "heavy-metal load",

    titleDefault: "Your Detox Plan",
    titleLight: "You’re In Good Shape",
    titleFirstTimer: "Start At The Beginning",
    titleFullKit: "Go For The Full Reset",
    titleTargetedOne: "Here’s Where To Start",
    titleTargetedMany: "Your Recommended Stack",

    overviewLight:
      "Honestly? Your answers don’t point to a heavy load right now — no need to overdo it. If you’d like a simple, gentle reset to stay ahead of it, here’s where we’d start. No pressure.",
    overviewFirstTimer:
      "Since this would be your first detox and a few areas lit up — mainly your {areas} — we’d start you at the beginning and work through it in the right order.",
    overviewFullKit:
      "A few areas stood out — mainly your {areas}. Rather than piece it together, the guided kit walks you through all four in the correct sequence.",
    overviewFullTail: "Here’s the plan, and exactly why.",
    overviewTargetedOne:
      "One thing stood out clearly: your {areas}. No need to overdo it — here’s the one detox we’d start with, and why.",
    overviewTargetedMany:
      "A couple of areas stood out — your {areas}. Here’s the order we’d run them in, and exactly why.",

    whyLiver:
      "Your liver is the front-line filter — your answers suggest it’s working overtime, so it’s the right first step.",
    whyKidney:
      "Your kidneys handle the flushing, and a few of your answers point to extra strain there.",
    whyParasite:
      "Our #1 seller. Several of your answers line up with common ways parasites get in and stick around.",
    whyMetal:
      "Everyday exposures add up — this one targets the heavy metals your answers flagged.",
    recLightPrimary:
      "A gentle 14-day reset for the body’s main filter — an easy, low-commitment place to begin.",
    recLightBinder:
      "Optional companion that helps escort what your liver loosens up out of the body.",
    recKit:
      "All four detoxes plus The Binder in the correct order — the complete reset, and the best value. It covers everything below in one guided protocol.",
    recStartSmaller:
      "Prefer to ease in? Start with just this one — it’s the single area that stood out most for you.",
    recBinder:
      "Take it alongside your detox — it binds what’s been loosened up so it leaves the body instead of recirculating.",
    recKitUpsell:
      "Doing more than one anyway? The full kit bundles all four detoxes and The Binder in the right order — and costs less than buying them apart.",

    chipLightReset: "a light maintenance reset",
    chipBinderPairsLiver: "pairs with the Liver Cleanse",
    chipBinderPairs: "pairs with {products}",
    chipKitSaves: "saves ${amount} vs. buying separately",
    chipCoversAll: "covers all four areas",
    chipUpsellSaves: "saves ${amount}",

    disclaimer:
      "These statements have not been evaluated by the Food & Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. This quiz is for educational purposes only and is not medical advice. Consult your healthcare provider before beginning any detox program, especially if you are pregnant, nursing, taking medication, or have a medical condition. © Boost Blenz · Blended in Golden, CO.",
  },

  // Intro A/B variants — all on brand purple; only copy/tone changes.
  intros: [
    { id: "intro-a", eyebrow: "Detox Quiz", title: "Find Your Detox In About 2 Minutes", sub: "Answer 8 quick questions and we’ll show you exactly which detox — or combo — fits your body and your habits.", note: "8 questions · ~2 min · instant results", cta: "Start the quiz" },
    { id: "intro-b", eyebrow: "Detox Quiz", title: "Do You Even Need To Detox?", sub: "No fear-mongering. Answer a few honest questions and we’ll tell you straight — recommending only what actually fits you.", note: "8 questions · ~2 min · see your results free", cta: "Find out" },
    { id: "intro-c", eyebrow: "Detox Quiz", title: "A Few Questions. We’ll Explain The Rest.", sub: "Common habits and everyday exposures add up. We’ll walk through yours and explain — in plain English — what your body may be working overtime on.", note: "8 questions · ~2 min · plain-English results", cta: "Begin" },
  ],

  themes: {
    liver: { bg: "#fff17f", ink: "#6b5400", label: "Liver & Filtering" },
    parasite: { bg: "#ff5b4a", ink: "#ffffff", label: "Gut & Parasites" },
    kidney: { bg: "#9ae0f5", ink: "#12484f", label: "Kidneys & Water" },
    metal: { bg: "#c8c2e8", ink: "#2d2470", label: "Exposure & Metals" },
    general: { bg: "#3c3291", ink: "#ffffff", label: "Your Habits" },
  },

  // Full-screen educational cards, interleaved between questions by `flow`.
  info: {
    basics: { bg: "#3c3291", ink: "#ffffff", illo: "illOrganSupport", eyebrow: "A quick primer", title: "Your body has a cleanup crew", body: "Your liver and kidneys filter everything you eat, drink, and breathe. The next few questions are about how hard they’re working.", cta: "Got it" },
    intake: { bg: "#ff5b4a", ink: "#ffffff", illo: "illTargetParasites", eyebrow: "What goes in", title: "Some things hitch a ride", body: "Certain foods and drinks are the most common way parasites and metals get into the body. This isn’t about fear — just how often it happens for you.", cta: "Makes sense" },
    environment: { bg: "#9ae0f5", ink: "#12484f", illo: "illRemoveMetals", eyebrow: "The world around you", title: "Exposure adds up quietly", body: "Your water, your city, even old dental fillings shape your heavy-metal load over time. A few quick ones about your environment.", cta: "Continue" },
  },

  questions: [
    {
      id: "diet", theme: "liver", image: "illOrganSupport",
      caption: "Your liver is the body’s main filter — what you eat is the biggest factor in how hard it’s working.",
      title: "How would you describe your everyday diet?", sub: "Pick the closest fit.", multi: false,
      options: [
        { id: "whole", label: "Mostly whole, home-cooked foods", score: {}, reasons: {} },
        { id: "mixed", label: "A mix of home cooking and takeout", score: { liver: 1, parasite: 1 }, reasons: { liver: "a mix of home & takeout meals", parasite: "eating out regularly" } },
        { id: "processed", label: "Lots of packaged or processed foods", score: { liver: 2, metal: 1 }, reasons: { liver: "a diet high in processed food", metal: "additives in packaged food" } },
        { id: "fastfood", label: "Frequent fast food or dining out", score: { liver: 2, parasite: 2 }, reasons: { liver: "frequent fast food", parasite: "food often prepared outside the home" } },
      ],
    },
    {
      id: "drinks", theme: "liver", image: "illOrganSupport",
      caption: "Alcohol and caffeine both route through the liver and kidneys — regular use means extra shifts.",
      title: "How’s your alcohol and caffeine?", sub: "Roughly, on a normal week.", multi: false,
      options: [
        { id: "heavy", label: "A few drinks a week and/or daily coffee", score: { liver: 2, kidney: 1 }, reasons: { liver: "regular alcohol & caffeine", kidney: "caffeine load on the kidneys" } },
        { id: "some", label: "The occasional drink or coffee", score: { liver: 1 }, reasons: { liver: "some alcohol & caffeine" } },
        { id: "none", label: "Rarely or never", score: {}, reasons: {} },
      ],
    },
    {
      id: "rawfoods", theme: "parasite", image: "illTargetParasites",
      caption: "Certain foods are the most common way parasites and heavy metals get in.",
      title: "Do any of these sound like you?", sub: "Select all that apply — or choose none.", multi: true,
      options: [
        { id: "sushi", label: "I eat sushi or raw / undercooked fish", score: { parasite: 2, metal: 1 }, reasons: { parasite: "eating raw or undercooked fish", metal: "raw fish, a common mercury source" } },
        { id: "meat", label: "I eat undercooked meat or street food", score: { parasite: 2 }, reasons: { parasite: "undercooked meat or street food" } },
        { id: "bigfish", label: "I eat large fish (tuna, swordfish) often", score: { metal: 2 }, reasons: { metal: "regular large predatory fish" } },
        { id: "none", label: "None of these", exclusive: true, score: {}, reasons: {} },
      ],
    },
    {
      id: "water", theme: "kidney", image: "illRemoveMetals",
      caption: "Water is how your kidneys flush everything out. Low intake makes that job harder.",
      title: "How much water do you drink on a normal day?", sub: "Your best guess is fine.", multi: false,
      options: [
        { id: "low", label: "Less than 4 cups", score: { kidney: 2 }, reasons: { kidney: "low daily water intake" } },
        { id: "mid", label: "Around 4–8 cups", score: { kidney: 1 }, reasons: { kidney: "moderate water intake" } },
        { id: "high", label: "8+ cups", score: {}, reasons: {} },
      ],
    },
    {
      id: "symptoms", theme: "parasite", image: "illTargetParasites",
      caption: "These are the everyday signals people notice when the body is carrying more than it can easily clear.",
      title: "Which of these do you notice regularly?", sub: "Select all that apply — or none.", multi: true,
      options: [
        { id: "digestion", label: "Bloating, gas, or irregular digestion", score: { parasite: 2, liver: 1 }, reasons: { parasite: "ongoing digestive issues", liver: "sluggish digestion" } },
        { id: "energy", label: "Low energy or brain fog", score: { liver: 2, metal: 1 }, reasons: { liver: "low energy & fatigue", metal: "brain fog" } },
        { id: "cravings", label: "Strong sugar or carb cravings", score: { parasite: 2 }, reasons: { parasite: "persistent sugar cravings" } },
        { id: "skin", label: "Skin breakouts or rashes", score: { liver: 1, parasite: 1 }, reasons: { liver: "skin flare-ups", parasite: "skin issues" } },
        { id: "focus", label: "Trouble with focus or memory", score: { metal: 2 }, reasons: { metal: "trouble concentrating" } },
        { id: "none", label: "None really", exclusive: true, score: {}, reasons: {} },
      ],
    },
    {
      id: "exposure", theme: "metal", image: "illRemoveMetals",
      caption: "Where you live and what you’re around quietly shapes your heavy-metal and parasite exposure.",
      title: "Anything here part of your daily life?", sub: "Select all that apply — or none.", multi: true,
      options: [
        { id: "tap", label: "I mostly drink tap water", score: { metal: 2, kidney: 1 }, reasons: { metal: "drinking tap water", kidney: "mineral load from tap water" } },
        { id: "fillings", label: "I have older metal dental fillings", score: { metal: 2 }, reasons: { metal: "older metal fillings" } },
        { id: "city", label: "I live in a city, near industry, or farmland", score: { metal: 1 }, reasons: { metal: "environmental exposure near home" } },
        { id: "pets", label: "I have pets or am around animals a lot", score: { parasite: 2 }, reasons: { parasite: "close contact with animals" } },
        { id: "travel", label: "I’ve traveled abroad in the past year", score: { parasite: 2 }, reasons: { parasite: "recent international travel" } },
        { id: "none", label: "None of these", exclusive: true, score: {}, reasons: {} },
      ],
    },
    {
      id: "history", theme: "general", image: "illBindRemove",
      caption: "If you’ve never done a full detox, starting at the beginning — in the right order — makes a real difference.",
      title: "Have you ever done a full detox before?", sub: "This shapes where we’d start you.", multi: false,
      options: [
        { id: "never", label: "Never — this would be my first", never: true, kit: 3, score: {}, reasons: {} },
        { id: "once", label: "Once or twice, a while ago", kit: 1, score: {}, reasons: {} },
        { id: "regular", label: "Yes, I detox regularly", score: {}, reasons: {} },
      ],
    },
    {
      id: "goal", theme: "general", image: "illBindRemove",
      caption: "Last one. This helps us lead with what matters most to you right now.",
      title: "What matters most to you right now?", sub: "Pick your top priority.", multi: false,
      options: [
        { id: "reset", label: "A full reset — start clean", goal: "reset", score: {}, reasons: {} },
        { id: "energy", label: "More energy day to day", goal: "energy", score: { liver: 1 }, reasons: { liver: "wanting more daily energy" } },
        { id: "digestion", label: "Better digestion and gut health", goal: "digestion", score: { parasite: 1 }, reasons: { parasite: "a focus on gut health" } },
        { id: "exposure", label: "Clearing what I’ve been exposed to", goal: "exposure", score: { metal: 1 }, reasons: { metal: "wanting to clear past exposure" } },
        { id: "curious", label: "Honestly, just seeing if I need it", goal: "curious", score: {}, reasons: {} },
      ],
    },
  ],

  // The page order the shopper actually walks. Info cards are positioned HERE,
  // which is why they have to live in the admin and not the theme — inserting a
  // question shifts the breaks, and only one system can own that sequence.
  flow: [
    { type: "info", key: "basics" },
    { type: "q", qid: "diet" },
    { type: "q", qid: "drinks" },
    { type: "info", key: "intake" },
    { type: "q", qid: "rawfoods" },
    { type: "q", qid: "water" },
    { type: "q", qid: "symptoms" },
    { type: "info", key: "environment" },
    { type: "q", qid: "exposure" },
    { type: "q", qid: "history" },
    { type: "q", qid: "goal" },
  ],

  // ⚠️ name/price/compareAt are DISPLAY FALLBACKS for when Shopify is unreachable.
  // The theme renders live values from the Shopify product object, so an active
  // discount shows automatically. Handles + variant IDs verified live 2026-07-18.
  products: {
    liver: { name: "Liver Cleanse", price: 34.0, days: "14-day cleanse", handle: "liver-cleanse-boost-blenz", variantId: "50494128292143", sku: "30_Day_Liver_BB", accent: "#fff17f" },
    kidney: { name: "Kidney Cleanse", price: 34.0, days: "14-day cleanse", handle: "kidney-cleanse-boost-blenz", variantId: "46974607491375", sku: "30_Day_Kidney_BB", accent: "#9ae0f5" },
    parasite: { name: "Parasite Detox", price: 34.0, days: "30-day cleanse", handle: "parasite-killer-detox-boost-blenz", variantId: "51051057479983", sku: "30_Day_Parasite_BB", accent: "#ff5b4a", badge: "MOST POPULAR" },
    metal: { name: "Heavy Metal Detox", price: 34.0, days: "30-day cleanse", handle: "heavy-metal-detox-boost-blenz", variantId: "50494144184623", sku: "30_Day_Metal_BB", accent: "#c8c2e8" },
    binder: { name: "The Binder", price: 22.0, days: "Pairs with your detox", handle: "the-binder-activated-charcoal-boost-blenz", variantId: "50748493791535", sku: "Binder_Caps_BB", accent: "#f2f2f2" },
    kit: { name: "4-Stage Detox Kit & Free Binder", price: 108.0, days: "Full guided protocol", handle: "4-stage-detox-kit-parasite-heavy-metal-liver-kidney-cleanse", variantId: "51341426295087", sku: "5_Detox_Kit_BB", accent: "#3c3291", badge: "BEST VALUE" },
  },
};
